import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';

import { useTemplateDetailsStore } from '@store/templateDetailsStore';
import useAvailableLanguageOptions from '@hooks/useAvailableLanguageOptions';
import SelectInput from '@common/components/SelectInput/SelectInput';

import StarIcon from '@mui/icons-material/Star';
import { copyObjectWithoutRef } from '@common/helpers/helpers';

const HeaderTypography = ({ fontWeight, children }) => (
  <Typography
    sx={{
      fontSize: '1rem',
      lineHeight: '1.5',
      letterSpacing: '0.00938em',
      color: (theme) => theme.palette.primary.main,
      fontWeight: fontWeight,
      padding: '6px 0px 6px 15px',
    }}
  >
    {children}
  </Typography>
);

const EditorHeader = ({ sx, children }) => {
  const { t } = useTranslation();
  const availableLangOptions = useAvailableLanguageOptions();

  const name = useTemplateDetailsStore((s) => s.name);
  const setFormData = useTemplateDetailsStore((s) => s.setFormData);
  const currentLanguage = useTemplateDetailsStore((s) => s.currentLanguage);

  const onLangChange = (newLang) => {
    setFormData({ value: newLang, field: 'currentLanguage' });
  };

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.secondary.light,
        display: 'flex',
        paddingLeft: '90px',
        justifyContent: 'flex-start',
        zIndex: 4,
        position: 'relative',
        ...sx,
      })}
    >
      {!name ? (
        <HeaderTypography>{t('template_empty_table_message')}</HeaderTypography>
      ) : (
        <>
          <HeaderTypography fontWeight='600'>{t('template')}:</HeaderTypography>
          <HeaderTypography>{name}</HeaderTypography>
          {children}
          <SelectInput
            options={availableLangOptions.map((option) => {
              const optionCopy = copyObjectWithoutRef(option);
              if (optionCopy?.isDefault) {
                optionCopy.label = (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t(optionCopy.label)}
                    <StarIcon sx={{ fontSize: '13px' }} />
                  </Box>
                );
              } else {
                optionCopy.label = t(optionCopy.label);
              }
              return optionCopy;
            })}
            accessors={{ valueAccessor: 'value', textAccessor: 'label' }}
            value={currentLanguage}
            onChange={(e) => onLangChange(e.target.value)}
            sx={{
              marginTop: '3px',
              fontSize: '13px',
              height: '30px',
              width: '150px',
              '& > div': {
                p: '4px 12px',
              },
              marginRight: '20px',
              marginLeft: 'auto',
            }}
          />
        </>
      )}
    </Box>
  );
};

EditorHeader.defaultProps = { languages: [] };

EditorHeader.propTypes = {
  setActiveLang: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.object),
  activeLang: PropTypes.string.isRequired,
};

export default EditorHeader;
